// Environment detection utilities

/**
 * Check if the app is running inside Telegram
 */
export const isTelegramEnvironment = (): boolean => {
    if (typeof window === 'undefined') return false;
    
    try {
      // Add additional conditions for more reliable detection
      // 1. Check if Telegram.WebApp object exists
      const hasTelegramWebApp = 'Telegram' in window && 
             typeof window.Telegram === 'object' && 
             window.Telegram !== null &&
             'WebApp' in window.Telegram;
             
      // 2. Check if WebApp is initialized with data
      const hasInitData = hasTelegramWebApp && 
                          typeof window.Telegram.WebApp.initData === 'string' && 
                          window.Telegram.WebApp.initData.length > 0;
                          
      // 3. Check UserAgent for TelegramWebApp
      const userAgentHasTelegram = navigator.userAgent.includes('TelegramWebApp') || 
                                  navigator.userAgent.includes('Telegram');
      
      // Require at least two of the three conditions to be true
      return hasTelegramWebApp && (hasInitData || userAgentHasTelegram);
    } catch {
      return false;
    }
  };
  
  /**
   * Check if the app is running as PWA
   */
  export const isPWAEnvironment = (): boolean => {
    if (typeof window === 'undefined') return false;
    
    try {
      // Check display mode
      const isStandaloneDisplay = window.matchMedia('(display-mode: standalone)').matches;
      
      // Check iOS standalone
      const isIOSStandalone = 'standalone' in window.navigator && 
                             Boolean(window.navigator.standalone);
      
      // Check Android app
      const isAndroidApp = document.referrer.includes('android-app://');
      
      return isStandaloneDisplay || isIOSStandalone || isAndroidApp;
    } catch {
      return false;
    }
  };
  
  /**
   * Check if the app is running in regular browser
   */
  export const isBrowserEnvironment = (): boolean => {
    return !isTelegramEnvironment() && !isPWAEnvironment();
  };
  
  /**
   * Get current environment type
   */
  export type EnvironmentType = 'telegram' | 'pwa' | 'browser';
  
  export const getCurrentEnvironment = (): EnvironmentType => {
    if (isTelegramEnvironment()) return 'telegram';
    if (isPWAEnvironment()) return 'pwa';
    return 'browser';
  };
  
  /**
   * Environment-specific configuration
   */
  export const getEnvironmentConfig = () => {
    const environment = getCurrentEnvironment();
    
    return {
      environment,
      showInstallPrompt: environment === 'browser',
      enableTelegramFeatures: environment === 'telegram',
      isPWA: environment === 'pwa',
      isTelegram: environment === 'telegram',
      isBrowser: environment === 'browser'
    };
  }; 